<script lang="ts">
	import { currentPageTrades, fetchingPaginatedData, paginatedTrades } from '../store';
	import UserHistoryItem from './UserHistoryItem.svelte';
	import { smartWalletCreationPending, walletConnected, walletLoaded } from '$lib/wallet/store';
	import { smartContractWalletAddress } from '../scwallet/store';
	import { quadIn } from 'svelte/easing';
	import { fly } from 'svelte/transition';
	import { getPaginatedTrades } from '$lib/data/userHoldings';
	import { nFormatter } from '$lib/util/util';

	let pageNumbersToShow: Array<number | '.......' | '...'> = [1];
	$: if ($walletConnected && $paginatedTrades && $paginatedTrades.trades.length > 0) {
		populatePageNumbersToShow();
	}

	function populatePageNumbersToShow() {
		if ($paginatedTrades.totalPages <= 8) {
			pageNumbersToShow = Array.from({ length: $paginatedTrades.totalPages }, (_, i) => i + 1);
		} else {
			pageNumbersToShow = [];
			pageNumbersToShow = pageNumbersToShow.concat([1, 2, 3]);

			if ($currentPageTrades > 3 && $currentPageTrades < $paginatedTrades.totalPages - 2) {
				pageNumbersToShow.push('...');
				pageNumbersToShow.push($currentPageTrades);
				pageNumbersToShow.push('...');
			} else {
				pageNumbersToShow.push('.......');
			}

			pageNumbersToShow = pageNumbersToShow.concat([
				$paginatedTrades.totalPages - 2,
				$paginatedTrades.totalPages - 1,
				$paginatedTrades.totalPages
			]);
		}
	}

	function nextPage() {
		if ($currentPageTrades < $paginatedTrades.totalPages) setCurrentPage($currentPageTrades + 1);
	}

	function previousPage() {
		if ($currentPageTrades > 1) setCurrentPage($currentPageTrades - 1);
	}

	function setCurrentPage(val: number) {
		$currentPageTrades = val;
		getPaginatedTrades();
	}
</script>

{#if $paginatedTrades && $paginatedTrades.trades.length > 0}
	<div class="hidden sm:grid grid-cols-7 px-2 text-fx-grey-400 mb-6">
		<div class="text-xs">Date</div>
		<div class="text-xs text-center">Perp</div>
		<div class="text-xs text-center">Side</div>
		<div class="text-xs text-center">
			Volume ${nFormatter($paginatedTrades.totalTradeVolume, 2)}
		</div>
		<div class="text-xs text-center">Execution Price</div>
		<div class="text-xs text-center">Accrued Funding</div>
		<div class="text-xs text-right">Realised PnL</div>
	</div>
{/if}
<div class="overflow-auto sm:max-h-[40vh] no-scrollbar">
	{#if $walletLoaded && $walletConnected && $smartContractWalletAddress && $fetchingPaginatedData}
		<div class="flex flex-col h-full gap-3 flex-grow">
			{#each Array.from(Array(7).keys()) as a}
				<div
					class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
				/>
			{/each}
		</div>
	{:else if $walletConnected && $paginatedTrades && $paginatedTrades.trades.length > 0}
		<div class="flex flex-col overflow-y">
			{#each $paginatedTrades.trades as eachTrade, i}
				<div
					in:fly={{
						duration: 300 * Math.sqrt(i),
						x: 0,
						y: 30,
						opacity: 0.1,
						easing: quadIn
					}}
				>
					<UserHistoryItem tra={eachTrade} />
				</div>
			{/each}
		</div>
	{:else}
		<div class="py-20 sm:py-10 h-full flex flex-col items-center justify-center">
			{#if $walletLoaded && $walletConnected && $smartContractWalletAddress}
				<img loading="lazy" src={'empty-position.png'} alt="" class="mx-auto max-w-[30%] mb-3" />
				<div class="text-center text-fx-grey-200 font-medium text-sm mb-2">
					No Trade History Found
				</div>
				<div class="w-full text-fx-grey-400 text-xs rounded-xl squircle-xl text-center">
					All your trading history will appear here.
				</div>
			{:else if $walletLoaded && !$walletConnected}
				<img loading="lazy" src={'empty-position.png'} alt="" class="mx-auto max-w-[30%] mb-3" />
				<div class="text-center text-fx-grey-200 font-medium text-sm mb-2">Connect wallet</div>
				<div class="w-full text-fx-grey-400 text-xs rounded-xl squircle-xl text-center">
					All your trading history will appear here.
				</div>
			{:else if $smartWalletCreationPending}
				<img loading="lazy" src={'empty-position.png'} alt="" class="mx-auto max-w-[30%] mb-3" />
				<div class="text-center text-fx-grey-200 font-medium text-sm mb-2">
					Create Smart Contract Wallet
				</div>
				<div class="w-full text-fx-grey-400 text-xs rounded-xl squircle-xl text-center">
					All your trading history will appear here.
				</div>
			{:else}
				<div class="grid gap-2">
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
				</div>
			{/if}
		</div>
	{/if}
</div>
{#if $paginatedTrades && $paginatedTrades.trades.length > 0}
	<div class="p-4 flex items-center justify-center gap-4">
		<button
			on:click={previousPage}
			on:keydown={previousPage}
			class="text-fx-grey-300 text-xs cursor-pointer hover:text-fx-white"
		>
			{'<'} Prev
		</button>
		{#each pageNumbersToShow as p}
			<button
				on:click={() => {
					if (typeof p == 'number') setCurrentPage(p);
				}}
				on:keydown={() => {
					if (typeof p == 'number') setCurrentPage(p);
				}}
				class=" text-xs cursor-pointer hover:text-fx-white"
				class:text-fx-white={$currentPageTrades == p}
				class:text-fx-grey-300={$currentPageTrades != p}
			>
				{p}
			</button>
		{/each}
		<button
			on:click={nextPage}
			on:keydown={nextPage}
			class="text-fx-grey-300 text-xs cursor-pointer hover:text-fx-white"
		>
			Next {'>'}
		</button>
	</div>
{/if}
